import { gql, useLazyQuery } from '@apollo/client';
import { OrganizationUserAccessTypes } from '@interfaces/organizationUser';
import { type RepCode, RepCodeState } from '@interfaces/repCode';
import type { SxProps } from '@mui/material';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { UserContext, usePermission } from '@providers/userContextProvider';
import { useContext, useEffect, useState } from 'react';

const MAX_SEARCH_RESULTS = 12;

export type SearchResults = { items: RepCode[]; count: number };
export type SearchFn = (query: string) => Promise<SearchResults>;

export const SEARCH_REP_CODES = gql`
  query searchRepCodes($searchText: String!, $organizationId: ObjectID!, $states: [RepCodeStates!], $organizationUserId: ObjectID) {
    fetchRepCodes(input: {
      filter: { searchText: $searchText, organizationId: $organizationId, states: $states, organizationUserId: $organizationUserId }
      pagination: { perPage: ${MAX_SEARCH_RESULTS} },
    }) {
      totalCount
      repCodes {
        code
        name
      }
    }
  }
`;

const matchingFn = (item: RepCode, value: string) =>
  [item.code, item.name].some((str) =>
    str?.toLowerCase()?.includes(value.toLowerCase()),
  );

const INITIAL_SEARCH_RESULT = {
  items: [],
  count: 0,
};

const ACTIVE_REP_CODE_STATES = [RepCodeState.ACTIVE];

export const RepCodeSelect = ({
  setRepCode,
  repCode,
  label,
  disabled,
  width,
  fullWidth,
  sx,
  error,
  errorText,
  autoFocus,
  onBlur,
  locked,
  lockMessage,
  testId,
}: {
  setRepCode: (repCode: RepCode | undefined) => void;
  repCode: Partial<RepCode> | undefined;
  label: string;
  disabled?: boolean;
  width?: string;
  fullWidth?: boolean;
  sx?: SxProps;
  error?: boolean;
  errorText?: string;
  autoFocus?: boolean;
  locked?: boolean;
  lockMessage?: string;
  onBlur?: (e: React.FocusEvent) => void;
  testId?: string;
}) => {
  const canReadRepCode = usePermission('read:rep_code');
  const { activeOrganization, userContext } = useContext(UserContext);
  const [searchResult, setSearchResult] = useState<SearchResults>(
    INITIAL_SEARCH_RESULT,
  );

  const organizationId = activeOrganization.id;
  const isRepCodeUser =
    userContext.accessType === OrganizationUserAccessTypes.REP_CODE;
  const organizationUserId = isRepCodeUser ? userContext.id : undefined;

  const [searchRepCodes, { loading }] = useLazyQuery(SEARCH_REP_CODES, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const searchFn = (query: string): Promise<SearchResults> =>
    new Promise<SearchResults>((resolve) => {
      searchRepCodes({
        variables: {
          searchText: query,
          organizationId,
          states: ACTIVE_REP_CODE_STATES,
          organizationUserId,
        },
        onCompleted: (data) => {
          resolve({
            items: data.fetchRepCodes.repCodes || [],
            count: data.fetchRepCodes.totalCount,
          });
        },
        onError: () => resolve(INITIAL_SEARCH_RESULT),
      });
    });

  useEffect(() => {
    searchFn('').then(setSearchResult);
  }, []);

  if (!canReadRepCode) return <></>;

  return (
    <Autocomplete
      width={width}
      data-testid={testId ?? 'rep-code-select-field'}
      freeSolo
      loading={loading}
      disabled={disabled}
      options={searchResult.items}
      fullWidth={fullWidth}
      label={label ?? ''}
      value={repCode}
      getOptionLabel={(option) => option?.code}
      isOptionEqualToValue={(option: RepCode, value: RepCode) =>
        option.code === value.code
      }
      renderOption={(props: any, option: RepCode) => (
        <li {...props}>{option.code}</li>
      )}
      onInputChange={async (_e: React.SyntheticEvent, value: string) =>
        setSearchResult(await searchFn(value))
      }
      onChange={(_e: React.SyntheticEvent, value: RepCode, reason: string) => {
        if (reason === 'clear') {
          setRepCode(undefined);
          return;
        }

        if (value?.code) {
          const result = searchResult.items.find(
            (item) => item.code === value.code,
          );
          setRepCode(result as RepCode);
        }
      }}
      onBlur={onBlur}
      filterOptions={(options: RepCode[], state: any) =>
        options.filter((item) => matchingFn(item, state?.inputValue))
      }
      componentsProps={{ popupIndicator: { sx: { display: 'none' } } }}
      InputProps={{ disabled }}
      inputReadOnly={!!repCode}
      sx={sx}
      error={error}
      errorText={errorText}
      autoFocus={autoFocus}
      locked={locked}
      lockMessage={lockMessage}
      disableClearable={!repCode}
    />
  );
};

export default RepCodeSelect;
